import React, { useState } from 'react';
import {
	Form,
	Popover,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh } from '@fortawesome/pro-thin-svg-icons';
import {
	CargoUnitLabels,
	CargoUnitTypes,
	Currencies,
	CurrencySymbols,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import type { CargoProps } from '@api/models/cargo';
import Button from '@client/components/Button';
import styles from '@client/screens/estimates/details/styles/shared.module.css';
import Select from '@client/components/Select';
import NumericInput from '@client/components/NumericInput';
import { useExchangeRates } from '@client/utils/hooks/useExchangeRates';
import { usePortRotationProvider } from './context/PortRotationContext';
import { LocalPortRotation } from './context/hooks/usePortRotationHandlers';

type Props = {
	portRotationId: number | undefined;
	currency: Values<typeof Currencies>;
	type: 'portCost' | 'linerCost';
	disabled?: boolean;
	cargo?: CargoProps;
};

const PortCostPopover = ({
	portRotationId,
	disabled,
	currency,
	type,
	cargo,
}: Props) => {
	const [showPopover, setShowPopover] = useState<boolean>(false);
	const [form] = Form.useForm();

	const { portRotation, onPortRotationRowChange } = usePortRotationProvider();
	const entry = portRotation.find((p) => p.id === portRotationId);
	const exchangeRates = useExchangeRates(currency);
	const isPortCost = type === 'portCost';
	const currencyKey = isPortCost ? 'portCostCurrency' : 'linerCostCurrency';
	const exchangeRateKey = isPortCost ? 'portCostExchangeRate' : 'linerCostExchangeRate';
	const title = isPortCost ? 'Port Cost' : 'Liner Cost';

	const handleSave = (
		values: {
			currency: Values<typeof Currencies>;
			exchangeRate: number;
			unit: Values<typeof CargoUnitTypes>;
		},
	) => {
		if (portRotationId == null) {
			return;
		}

		onPortRotationRowChange(
			portRotationId,
			{
				[currencyKey]: values.currency,
				[exchangeRateKey]: values.exchangeRate,
				...(!isPortCost ? { linerCostUnit: values.unit } : {}),
			} as { [key: string]: any } as LocalPortRotation,
		);

		setShowPopover(false);
	};

	const refreshExchangeRate = () => {
		const selectedCurrency = form.getFieldValue('currency') as Values<typeof Currencies>;

		if (exchangeRates && selectedCurrency) {
			const currentRate = exchangeRates[selectedCurrency];

			if (currentRate != null) {
				form.setFieldsValue({ exchangeRate: currentRate });
			}
		}
	};

	const handleValuesChange = (changedValues: any) => {
		if (changedValues.currency) {
			const selectedCurrency = changedValues.currency;
			const currentRate = exchangeRates[selectedCurrency as Values<typeof Currencies>];

			if (currentRate != null) {
				form.setFieldsValue({ exchangeRate: currentRate });
			}
		}
	};

	const differentUnits = cargo?.unit !== cargo?.secondaryUnit;

	const content = (
		<div className={styles.configContent}>
			<Form
				form={form}
				onFinish={handleSave}
				initialValues={{
					currency: entry?.[currencyKey],
					exchangeRate: entry?.[exchangeRateKey] ??
					exchangeRates?.[entry?.[currencyKey] as Values<typeof Currencies>],
					unit: entry?.linerCostUnit,
				}}
				onValuesChange={handleValuesChange}
			>
				{differentUnits && (
					<Form.Item
						label="Unit"
						name="unit"
						className={styles.configSwitchLarge}
					>
						<Select
							className={styles.configSelectField}
							options={[
								{
									key: 1,
									label: CargoUnitLabels[cargo?.unit ?? 'MT'],
									value: cargo?.unit,
								},
								{
									key: 2,
									label: CargoUnitLabels[cargo?.secondaryUnit ?? 'MT'],
									value: cargo?.secondaryUnit,
								},
							]}
						/>
					</Form.Item>
				)}
				<Form.Item
					label="Currency"
					name="currency"
					className={styles.configSwitchLarge}
				>
					<Select
						className={styles.configSelectField}
						showSearch
						data-testid="select-portcost-currency"
						options={Object.keys(Currencies).map((c) => ({
							label: c,
							value: c,
							'data-testid': c,
						}))}
					/>
				</Form.Item>
				<Form.Item
					label="Exchange Rate"
					name="exchangeRate"
					className={styles.configSwitchLarge}
				>
					<NumericInput className={styles.configNumberFieldLarge} />
				</Form.Item>
				<Button
					onClick={refreshExchangeRate}
					icon={(<FontAwesomeIcon color="#1677ff" icon={faRefresh} />)}
				/>
				<Form.Item>
					<div className={styles.configSubmitContainer}>
						<Button
							data-testid="save-cost-button"
							type="primary"
							htmlType="submit"
						>
							Save
						</Button>
					</div>
				</Form.Item>
			</Form>
		</div>
	);

	if (portRotationId == null) {
		return null;
	}

	if (!entry || !exchangeRates) {
		return null;
	}

	return (
		<Popover
			title={`${title} Settings`}
			open={showPopover}
			onOpenChange={(visible) => {
				setShowPopover(visible);

				if (visible && entry) {
					form.setFieldsValue({
						currency: entry[currencyKey],
						unit: entry.linerCostUnit,
						exchangeRate:
							entry[exchangeRateKey] ??
							exchangeRates?.[entry[currencyKey] as Values<typeof Currencies>],
					});
				}
			}}
			content={content}
			destroyTooltipOnHide
		>
			<Button
				className={styles.button}
				disabled={disabled}
				type="link"
				icon={CurrencySymbols[
					entry?.[currencyKey] as keyof typeof CurrencySymbols
				] ?? entry?.[currencyKey]}
				size="small"
			/>
		</Popover>
	);
};

export default PortCostPopover;
